import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { CreatePresentRequest, CreatePresentResponse } from '../classes/Present';
import { AlertController, NavController } from '@ionic/angular';
import { CreateNewVariantRequest, CreateNewVariantResponse, DeleteVariantRequest, DeleteVariantResponse, RecreateVariantRequest } from '../classes/SongVariant';
import { SongTier } from '../classes/Song';

export enum HttpErrorCode {
  NO_CREDITS_SONG = "NO_CREDITS_SONG",
  NO_CREDITS_VARIANT = "NO_CREDITS_VARIANT",
  NO_CREDITS_COVER_IMAGE = "NO_CREDITS_COVER_IMAGE",
  UNAUTHORIZED = "UNAUTHORIZED",
  PREVIEW_LIMIT_USER = "PREVIEW_LIMIT_USER",
  PREVIEW_LIMIT_DAILY = "PREVIEW_LIMIT_DAILY",
}

export type SongBackendStatus =
  | 'created'
  | 'payment_pending'
  | 'paid_pending_generation'
  | 'generating_lyrics'
  | 'generating_audio'
  | 'ready'
  | 'failed';


export interface ImageJobRequest {
    songId: string,
    variantId: string;
    varVersion: number; // 0 | 1
    type: string;
    hint: string;
    quality: "low" | "medium" | "high";
}
export interface ImageJobResponse {
  jobId: string;
}
// Credits Checkout

export interface CreditsCheckoutPayload {
  packId: string;
  successUrl: string;
  cancelUrl: string;
}

export interface CreditsCheckoutResponse {
  checkoutUrl: string;
  orderId: string;
}

// Regeneration (optional)

export interface SongRegenerationPayload {
  songId: string;
  mode?: 'full' | 'lyrics_only' | 'audio_only';
}

export interface SongRegenerationResponse {
  songId: string;
  status: SongBackendStatus;
}

export type ClaimSongResult =
  | 'claimed'
  | 'already-yours'
  | 'belongs-to-account'
  | 'invalid'
  /** Zurueckholbar, aber es fehlt die Sitzung - siehe handleClaimToken. */
  | 'claimable';

@Injectable({
  providedIn: 'root'
})
export class BackendService {
  private functions = inject(Functions);

  constructor(
    private navCtrl: NavController,
    private alertCtrl: AlertController,
  ) {}

  // --------------------------------------
  // SONG CREATION
  // --------------------------------------

  /**
   * Song-Generierung aus dem Funnel anstoßen.
   * Backend:
   *  - prüft Auth + Credits
   *  - bucht Credit
   *  - legt Song-Dokument an
   *  - startet asynchronen LLM/Suno-Job
   */
  /**
   * Haengt den Besitz eines anonymen Kontos auf das jetzt angemeldete um.
   *
   * Noetig, wenn jemand anonym einen Song erzeugt und sich danach mit Google
   * oder Apple anmeldet, obwohl es mit dieser Adresse schon ein Konto gibt.
   * Firebase kann die Identitaeten dann nicht verknuepfen, der Client meldet
   * am bestehenden Konto an - und der gerade erzeugte Song waere verwaist.
   *
   * Das `anonIdToken` muss **vor** dem Sitzungswechsel geholt worden sein; es
   * ist der Beleg dafuer, dass der Aufrufer das anonyme Konto kontrolliert hat.
   */
  async claimAnonymousAccount(
    anonIdToken: string
  ): Promise<{ movedByCollection: Record<string, number>; anonUserId: string }> {
    const callable = httpsCallable<
      { anonIdToken: string },
      { movedByCollection: Record<string, number>; anonUserId: string }
    >(this.functions, 'claimAnonymousAccount');

    const result = await callable({ anonIdToken });
    return result.data;
  }

  /**
   * Holt die abspielbare URL einer Version.
   *
   * Fuer den registrierten Besitzer einer noch nicht gekauften Vorschau ist das
   * die Volldatei - je Version genau einmal. In allen anderen Faellen kommt
   * der 40-Sekunden-Schnitt zurueck.
   *
   * **Jeder Aufruf zieht das Kontingent dieser Version.** Der Aufrufer muss das
   * Ergebnis fuer die Sitzung merken, sonst verbrennt ein blosser Reload den
   * einzigen vollen Durchlauf.
   */
  async getPlaybackUrl(
    variantId: string,
    version: number
  ): Promise<{ url: string; full: boolean }> {
    const callable = httpsCallable<
      { variantId: string; version: number },
      { url: string; full: boolean }
    >(this.functions, 'getPlaybackUrl');

    const result = await callable({ variantId, version });
    return result.data;
  }

  /**
   * Holt einen Song ueber den Schluessel aus der "fertig"-Mail zurueck.
   *
   * Umgehaengt wird nur, solange der Song einem anonymen Konto gehoert -
   * gehoert er schon einem registrierten, ist Anmelden der richtige Weg. Der
   * Schluessel wird in jedem entschiedenen Fall verbraucht.
   */
  async claimSongByToken(
    songId: string,
    claimToken: string
  ): Promise<{ result: ClaimSongResult }> {
    const callable = httpsCallable<
      { songId: string; claimToken: string },
      { result: ClaimSongResult }
    >(this.functions, 'claimSongByToken');

    const result = await callable({ songId, claimToken });
    return result.data;
  }

  /**
   * Meldet, wie lange die Volldatei tatsaechlich gehoert wurde.
   *
   * Gezaehlt wird Hoerzeit, nicht die Zahl der Aufrufe - ein Seitenwechsel
   * oder eine Pause darf das Kontingent nicht verbrauchen.
   */
  async reportFullPlayback(
    variantId: string,
    version: number,
    seconds: number
  ): Promise<{ capReached: boolean }> {
    const callable = httpsCallable<
      { variantId: string; version: number; seconds: number },
      { capReached: boolean }
    >(this.functions, 'reportFullPlayback');

    const result = await callable({ variantId, version, seconds });
    return result.data;
  }

  async createNewSong(
    payload: CreateNewVariantRequest
  ): Promise<CreateNewVariantResponse> {
    if(!payload.songId && !payload.tier) {
      throw new Error("SongTier not set");
    }

    const callable = httpsCallable<CreateNewVariantRequest, CreateNewVariantResponse>(
      this.functions,
      'createNewSong'
    );

    const result = await callable(payload);
    return result.data;
  }
  /**
   * Song-Re-Generierung aus bereits existirendem Song anstoßen.
   * Backend:
   *  - prüft Auth + Credits
   *  - bucht Credit
   *  - legt Song-Dokument an
   *  - startet asynchronen Suno-Job mit veränderten Lyric / Styles
   */
  async createNewVariant(
    payload: RecreateVariantRequest
  ): Promise<CreateNewVariantResponse> {
    const callable = httpsCallable<RecreateVariantRequest, CreateNewVariantResponse>(
      this.functions,
      'createNewVariant'
    );

    const result = await callable(payload);
    return result.data;
  }

  // --------------------------------------
  // CREDITS / CHECKOUT
  // --------------------------------------

  /**
   * Checkout-Session für den Kauf eines Credit-Pakets erstellen.
   */
  async createCreditsCheckoutSession(
    payload: CreditsCheckoutPayload
  ): Promise<CreditsCheckoutResponse> {
    const callable = httpsCallable<CreditsCheckoutPayload, CreditsCheckoutResponse>(
      this.functions,
      'credits_createCheckoutSession'
    );

    const result = await callable(payload);
    return result.data;
  }

  // --------------------------------------
  // SONG REGENERATION (optional)
  // --------------------------------------

  /**
   * Erneute Generierung für einen bestehenden Song anstoßen.
   */
  async requestSongRegeneration(
    payload: SongRegenerationPayload
  ): Promise<SongRegenerationResponse> {
    const callable = httpsCallable<SongRegenerationPayload, SongRegenerationResponse>(
      this.functions,
      'song_requestRegeneration'
    );

    const result = await callable(payload);
    return result.data;
  }


  /**
   * Frage Username zu einer UserId über öffentliche API
   */
  async getUsernameById(userId: string): Promise<string> {
    const fn = httpsCallable<{ userId: string }, { username: string }>(
      this.functions,
      'getUsernameById'
    );

    const result = await fn({ userId });

    return result.data.username;
  }


  /**
   * Variante endgueltig loeschen. Nur Admins, das Backend prueft die Rolle.
   *
   * Wirft, wenn eine Geschenkseite oder ein Share-Link auf die Variante zeigt
   * oder es die letzte des Songs ist. Die Begruendung steht in der Meldung und
   * gehoert unveraendert an den Aufrufer weitergereicht.
   */
  async deleteVariant(variantId: string): Promise<DeleteVariantResponse> {
    const fn = httpsCallable<DeleteVariantRequest, DeleteVariantResponse>(
      this.functions,
      'deleteVariant'
    );

    const result = await fn({ variantId });
    return result.data;
  }

  /**
   * Generiere neues Cover
   */
  async requestCreateImageJob(
    payload: ImageJobRequest
  ): Promise<ImageJobResponse> {
    const callable = httpsCallable<ImageJobRequest, ImageJobResponse>(
      this.functions,
      'createImageJob'
    );

    const result = await callable(payload);
    console.log("createNewCover", result.data);
    return result.data;
  }

  async processBackendError(errObj: any) {
    let errMsg;
    let errButtons: any = [{ text: "Ok", role: "cancel" }];
    let errHeader;
    const code: HttpErrorCode = errObj?.details?.code;
    if(code == HttpErrorCode.NO_CREDITS_SONG) {
      errHeader = "Kein Guthaben";
      errMsg = "Du hast leider kein Guthaben mehr, um neue Songs zu erstellen.";
      errButtons = [
        { text: "Abbrechen", role: "cancel" },
        {
          text: "Guthaben aufladen",
          handler: async () => {
            // this.navCtrl.navigateRoot(["song-plans"], { animated: true });
            this.navCtrl.navigateForward(["song-plans"]);
            return true;
          },
        },
      ]
    } else if(code == HttpErrorCode.PREVIEW_LIMIT_USER) {
      // Kein Fehler, sondern der Moment zu verkaufen: die kostenlosen
      // Vorschauen sind aufgebraucht, der naechste Song ist ein echter.
      errHeader = "Vorschauen aufgebraucht";
      errMsg = "Du hast deine kostenlosen Vorschauen aufgebraucht. Wähle ein Paket, um einen Song in voller Länge zu erstellen – und dein Vorschauen-Kontingent zurückzusetzen.";
      errButtons = [
        { text: "Später", role: "cancel" },
        {
          text: "Pakete ansehen",
          handler: async () => {
            this.navCtrl.navigateForward(["song-plans"]);
            return true;
          },
        },
      ]
    } else if(code == HttpErrorCode.PREVIEW_LIMIT_DAILY) {
      // Systemseitige Notbremse - der Nutzer kann nichts dafuer, also
      // hier auch kein Kaufangebot unterschieben.
      errHeader = "Gerade sehr viel los";
      errMsg = "Wir erstellen im Moment ungewöhnlich viele Songs. Bitte versuche es in ein paar Stunden noch einmal – dein ausgefülltes Formular bleibt gespeichert.";
    } else {
      if(errObj.message && typeof errObj.message === 'string') {
        errMsg = errObj.message;
      } else {
        errMsg = 'Beim Erstellen deiner Anfrage ist ein Fehler aufgetreten. Bitte versuche es erneut.';
      }
    }
    const alert = await this.alertCtrl.create({
        header: errHeader,
        message: errMsg,
        buttons: errButtons,
    });
    await alert.present();
  }

  async incrementPlay(variantId: string, varIndex: number) {
    await httpsCallable(this.functions,'incrementPlay')(
      {
      variantId,
      varIndex
    });
  }
}
